import { useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import type { useSegmentCounts } from "../api/useSegmentCounts";
import { SEGMENT_TAB } from "../constants";
import { getSegmentTab, withSegmentTab } from "../segmentTabParams";
import { AnimatedCount } from "./AnimatedCount";
import { BuilderTab } from "./BuilderTab";
import { VacanciesTab } from "./VacanciesTab";
import type { BuilderDraft } from "../hooks/useBuilderDraft";

import { Badge } from "@/components/ui/v2/badge";
import Tabs from "@/components/ui/v2/tabs";
import { SegmentHistoryTab } from "@/features/segments/history/SegmentHistoryTab";
import {
  SEGMENT_TOUR_TARGET,
  type TourDemoControl,
} from "@/features/segments/tour/constants";
import type { SegmentField } from "@/features/segments/types";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { cn } from "@/lib/utils/cn";

type SegmentBuilderTabsProps = {
  segmentId?: string;
  clientId?: string;
  fields: SegmentField[];
  readOnly: boolean;
  counts: ReturnType<typeof useSegmentCounts>;
  draft: BuilderDraft;
  activeConditionKey: string | null;
  onActiveConditionChange: (key: string | null) => void;
  tourDemoControl: TourDemoControl | null;
};

export function SegmentBuilderTabs({
  segmentId,
  clientId,
  fields,
  readOnly,
  counts,
  draft,
  activeConditionKey,
  onActiveConditionChange,
  tourDemoControl,
}: SegmentBuilderTabsProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabListRef = useTourTarget<HTMLDivElement>(SEGMENT_TOUR_TARGET.TABS);

  const activeTab = getSegmentTab(searchParams);

  const hasSwitchedTab = useRef(false);

  const handleTabChange = ({ id }: { id?: string }) => {
    if (!id) return;
    hasSwitchedTab.current = true;
    setSearchParams((prev) => withSegmentTab(prev, id), { replace: true });
  };

  const vacanciesCount = draft.listCounts.data?.includedCount;
  const vacanciesLabel = (
    <span className="inline-flex items-center gap-2">
      {t("common.vacancies")}
      <span aria-live="polite">
        {vacanciesCount !== undefined && (
          <Badge
            variant="default"
            size="small"
            shape="pill"
            data-testid="segment-count"
          >
            <AnimatedCount value={vacanciesCount} />
          </Badge>
        )}
      </span>
    </span>
  );

  const tabs = [
    {
      id: SEGMENT_TAB.RULES,
      label: t("admin.segments.builder.tabs.builder"),
      content: (
        <TabPanel fadeIn={hasSwitchedTab.current}>
          <BuilderTab
            readOnly={readOnly}
            clientId={clientId}
            fields={fields}
            counts={counts}
            draft={draft}
            activeConditionKey={activeConditionKey}
            onActiveConditionChange={onActiveConditionChange}
            tourDemoControl={tourDemoControl}
          />
        </TabPanel>
      ),
    },
    {
      id: SEGMENT_TAB.VACANCIES,
      label: vacanciesLabel,
      content: (
        <TabPanel fadeIn={hasSwitchedTab.current}>
          <VacanciesTab
            clientId={clientId}
            readOnly={readOnly}
            draftBody={draft.body}
            listCounts={draft.listCounts}
          />
        </TabPanel>
      ),
    },
    ...(segmentId
      ? [
        {
          id: SEGMENT_TAB.HISTORY,
          label: t("admin.segments.builder.tabs.history"),
          content: (
            <TabPanel fadeIn={hasSwitchedTab.current}>
              <SegmentHistoryTab segmentId={segmentId} />
            </TabPanel>
          ),
        },
      ]
      : []),
  ];

  return (
    <Tabs
      tabListRef={tabListRef}
      activeTabId={activeTab}
      onChange={handleTabChange}
      tabs={tabs}
    />
  );
}

function TabPanel({
  children,
  fadeIn,
}: {
  children: ReactNode;
  fadeIn: boolean;
}) {
  return (
    <div
      className={cn(
        fadeIn &&
          "animate-in fade-in-0 duration-300 ease-out motion-reduce:animate-none",
      )}
    >
      {children}
    </div>
  );
}
